// FILE: src/features/progressSlice.js
// Per-resource progress and the dashboard summary. Time is never sent from
// here: the learning-session heartbeats (useLearningSession) are the only
// accumulator, and every figure below is the server's (plan WP-3, AD-2).
//   GET   /api/progress                      → chaptersViewed / homelinksViewed
//   GET   /api/progress/summary              → today, cap, break, totals, enrolment
//   PATCH /api/progress/resources/:id/score  → quiz score from the player
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import fetchWithAuth from "../utils/fetchWithAuth";
import { timeStatusReceived } from "./timeSlice";

// Quiz result read from the player (bridge `resultpercent` or SCORM
// cmi.score.scaled). Still client-reported until the SCORM runtime (WP-4).
export const recordResourceScore = createAsyncThunk(
  "progress/recordResourceScore",
  async ({ resourceId, percent }, { rejectWithValue }) => {
    try {
      return await fetchWithAuth(`/api/progress/resources/${resourceId}/score`, {
        method: "PATCH",
        body: JSON.stringify({ percent })
      });
    } catch (err) {
      return rejectWithValue(err.message || "Failed to record score");
    }
  }
);

export const fetchProgress = createAsyncThunk(
  "progress/fetchProgress",
  async (_, { rejectWithValue }) => {
    try {
      return await fetchWithAuth("/api/progress");
    } catch (err) {
      return rejectWithValue(err.message || "Failed to fetch progress");
    }
  }
);

export const fetchProgressSummary = createAsyncThunk(
  "progress/fetchProgressSummary",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const summary = await fetchWithAuth("/api/progress/summary");
      dispatch(timeStatusReceived(summary));
      return summary;
    } catch (err) {
      return rejectWithValue(err.message || "Failed to fetch progress summary");
    }
  }
);

const progressSlice = createSlice({
  name: "progress",
  initialState: {
    loading: false,
    error: null,
    chaptersViewed: [],
    homelinksViewed: [],
    summary: null
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchProgress.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProgress.fulfilled, (state, action) => {
        state.loading = false;
        const { chaptersViewed, homelinksViewed } = action.payload || {};
        if (chaptersViewed !== undefined) state.chaptersViewed = chaptersViewed;
        if (homelinksViewed !== undefined) state.homelinksViewed = homelinksViewed;
      })
      .addCase(fetchProgress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error fetching progress";
      })
      .addCase(fetchProgressSummary.fulfilled, (state, action) => {
        state.summary = action.payload || null;
      });
  }
});

export default progressSlice.reducer;
